import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import AdminLayout from "@/components/AdminLayout";
import { toast } from "sonner";
import { Wallet, CheckCircle2, Clock, RefreshCw, Search } from "lucide-react";

const TABS = [
    { key: "pending", label: "Bekleyenler", icon: Clock },
    { key: "completed", label: "Tamamlananlar", icon: CheckCircle2 },
];

const fmtTRY = (n) => new Intl.NumberFormat("tr-TR", { style: "currency", currency: "TRY", minimumFractionDigits: 2 }).format(Number(n || 0));

export default function AdminRefundsPage() {
    const [tab, setTab] = useState("pending");
    const [items, setItems] = useState([]);
    const [pendingCount, setPendingCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const [q, setQ] = useState("");

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await api.get("/admin/refund-requests", { params: { status: tab, limit: 200 } });
            setItems(r.data?.items || []);
            setPendingCount(r.data?.pending_count || 0);
        } catch (e) {
            toast.error(e.response?.data?.detail || e.message);
        } finally {
            setLoading(false);
        }
    }, [tab]);

    useEffect(() => { load(); }, [load]);

    const complete = async (rr) => {
        const method = window.prompt("Ödeme yöntemi (bank_transfer, cash, card):", "bank_transfer");
        if (!method) return;
        const paymentRef = window.prompt(`Banka referans / Dekont no:\n\nMüşteri: ${rr.guest_name}\nTutar: ${fmtTRY(rr.refund_amount)}`, "");
        if (paymentRef === null) return;
        const note = window.prompt("Not (opsiyonel):", "") || "";
        if (!window.confirm(`İade yapıldı olarak işaretle:\n\nMüşteri: ${rr.guest_name}\nTutar: ${fmtTRY(rr.refund_amount)}\nDekont: ${paymentRef}\n\nDevam edilsin mi?`)) return;
        try {
            await api.post(`/admin/refund-requests/${rr.id}/complete`, { payment_ref: paymentRef, method, note });
            toast.success("İade tamamlandı. Rezervasyon 'İade Edildi' durumuna geçirildi.");
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || e.message);
        }
    };

    const filtered = items.filter((x) => {
        if (!q) return true;
        const s = q.toLowerCase();
        return (x.guest_name || "").toLowerCase().includes(s) ||
               (x.guest_email || "").toLowerCase().includes(s) ||
               (x.voucher_no || "").toLowerCase().includes(s) ||
               (x.hotel_name || "").toLowerCase().includes(s);
    });

    return (
        <AdminLayout title="İade Bekleyenler" action={
            <button onClick={load} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-xs font-semibold hover:bg-slate-50 dark:hover:bg-slate-800" data-testid="refunds-refresh">
                <RefreshCw className="h-3.5 w-3.5" /> Yenile
            </button>
        }>
            <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-1.5 flex gap-1.5 mb-4 w-fit">
                {TABS.map((t) => {
                    const Icon = t.icon;
                    const active = tab === t.key;
                    return (
                        <button key={t.key} onClick={() => setTab(t.key)} data-testid={`tab-${t.key}`}
                            className={`flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-bold transition-colors ${active ? "bg-violet-500 text-white" : "text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800"}`}>
                            <Icon className="h-4 w-4" />
                            {t.label}
                            {t.key === "pending" && pendingCount > 0 && (
                                <span className={`text-[10px] px-1.5 py-0.5 rounded-md font-bold ${active ? "bg-white/20" : "bg-orange-500 text-white"}`}>{pendingCount}</span>
                            )}
                        </button>
                    );
                })}
            </div>

            <div className="mb-3 flex items-center gap-2">
                <div className="relative flex-1 max-w-md">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <input
                        type="text"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Müşteri, e-posta, voucher, otel ara…"
                        className="w-full pl-9 pr-3 py-2 rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
                        data-testid="refunds-search"
                    />
                </div>
                <span className="text-xs text-slate-500">{filtered.length} kayıt</span>
            </div>

            <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-slate-50 dark:bg-slate-900/50 text-[10px] font-bold uppercase tracking-widest text-slate-500">
                            <tr>
                                <th className="text-left px-3 py-3">Voucher</th>
                                <th className="text-left px-3 py-3">Müşteri</th>
                                <th className="text-left px-3 py-3">Otel</th>
                                <th className="text-right px-3 py-3">Ödenen</th>
                                <th className="text-right px-3 py-3">İade Tutarı</th>
                                <th className="text-left px-3 py-3">Neden</th>
                                <th className="text-left px-3 py-3">Tarih</th>
                                {tab === "pending" ? <th className="text-center px-3 py-3">İşlem</th> : <th className="text-left px-3 py-3">Yapan / Dekont</th>}
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan={8} className="text-center py-12 text-slate-500">Yükleniyor…</td></tr>
                            ) : filtered.length === 0 ? (
                                <tr><td colSpan={8} className="text-center py-12 text-slate-500">
                                    <Wallet className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                                    {tab === "pending" ? "Bekleyen iade talebi yok 🎉" : "Tamamlanan iade kaydı yok"}
                                </td></tr>
                            ) : filtered.map((rr) => (
                                <tr key={rr.id} data-testid={`refund-row-${rr.id}`} className="border-t border-slate-100 dark:border-slate-800/50 hover:bg-slate-50/50 dark:hover:bg-slate-900/30">
                                    <td className="px-3 py-3 font-mono text-xs">{rr.voucher_no}</td>
                                    <td className="px-3 py-3">
                                        <div className="font-semibold text-slate-800 dark:text-slate-100">{rr.guest_name}</div>
                                        <div className="text-[11px] text-slate-500">{rr.guest_email} {rr.guest_phone && `· ${rr.guest_phone}`}</div>
                                    </td>
                                    <td className="px-3 py-3 text-xs">{rr.hotel_name || "—"}</td>
                                    <td className="px-3 py-3 text-right text-xs text-slate-600 dark:text-slate-300">{fmtTRY(rr.total_paid)}</td>
                                    <td className="px-3 py-3 text-right font-bold text-orange-600 dark:text-orange-400">{fmtTRY(rr.refund_amount)}</td>
                                    <td className="px-3 py-3 text-xs text-slate-600 dark:text-slate-300 max-w-[200px] truncate" title={rr.reason}>{rr.reason || "—"}</td>
                                    <td className="px-3 py-3 text-[11px] text-slate-500">{(rr.requested_at || "").slice(0, 10)}<br /><span className="text-[10px]">{rr.requested_by}</span></td>
                                    {tab === "pending" ? (
                                        <td className="px-3 py-3 text-center">
                                            <button onClick={() => complete(rr)} data-testid={`complete-${rr.id}`} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-bold">
                                                <CheckCircle2 className="h-3.5 w-3.5" /> İade Yapıldı
                                            </button>
                                        </td>
                                    ) : (
                                        <td className="px-3 py-3 text-[11px] text-slate-600 dark:text-slate-300">
                                            <div className="font-semibold">{rr.completed_by}</div>
                                            <div className="text-slate-400">{rr.payment_ref || "—"}</div>
                                            <div className="text-slate-400">{(rr.completed_at || "").slice(0, 10)}</div>
                                        </td>
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </AdminLayout>
    );
}
